import {
	Component,
	OnInit,
	ViewEncapsulation,
	OnDestroy,
	Input,
	ContentChildren,
	AfterContentInit,
	ViewChild,
	ElementRef,
	QueryList,
	AfterViewChecked,
	Output,
	EventEmitter
} from '@angular/core';
import { IdService } from '../../shared/services/id.service';
import { TabContentComponent } from './tab-content/tab-content.component';
import { ITransitionEvent } from '../transition/transition.types';

@Component({
	selector: 'rbc-tab',
	templateUrl: './tab.component.html',
	styleUrls: ['./tab.component.scss'],
	encapsulation: ViewEncapsulation.None
})
export class TabComponent implements OnInit, OnDestroy, AfterContentInit, AfterViewChecked {
	// Input Parameters
	@Input() id: string;
	@Input() default: number | string;
	@Input() disabled: boolean = false;
	@Input() scrollable: boolean = false;
	@Input() scrollColor: string;
	@Input() small: boolean;
	@Input() transition: boolean = true;
	@Input() flush: boolean;
	@Input() showAll: boolean;
	@Input() showAllText: string = 'Show all';
	@Input() showAllFirst: boolean;
	@Input() current: object;
	@Input() arrowLeftLabel: string = 'Move left';
	@Input() arrowRightLabel: string = 'Move right';

	@ContentChildren(TabContentComponent) tabs: QueryList<TabContentComponent>;
	@ViewChild('tabList') tabList: ElementRef;
	@ViewChild('tabWrap') tabWrap: ElementRef;

	@Output() tabupdate = new EventEmitter<{ active: number | string; id: string }>();

	direction: string = 'right';
	scrollDisabledLeft: boolean = true;
	scrollDisabledRight: boolean = false;
	isScrollable: boolean = false;
	contentTransitionState: boolean = true;
	nextActiveTab: TabContentComponent;
	nextActiveIndex: number;
	lastActiveIndex: number;

	constructor(private idService: IdService) {}

	ngOnInit() {
		this.isScrollable = this.scrollable;
		this.id = this.id ? this.id : this.idService.generate();
	}

	ngAfterContentInit() {
		const defaultTabIndex = this.default || 0;
		this.tabs.toArray().forEach((tab, index) => {
			if (index === defaultTabIndex) {
				tab.isActive = true;
			}
			tab.index = index;
		});
	}

	ngAfterViewChecked() {
		this.determineIfScrollable();
	}

	ngOnDestroy(): void {
		this.idService.remove(this.id);
	}

	determineIfScrollable() {
		// even if scrollable is off, tabs should be accessible on small viewports through scrollability
		if (this.tabList && this.tabWrap) {
			this.isScrollable = !(
				(this.tabList.nativeElement as HTMLElement).scrollWidth <=
				(this.tabWrap.nativeElement as HTMLElement).offsetWidth
			);
		}
	}

	calculateClasses() {
		const classes = {};
		classes['tab-disabled'] = this.disabled;
		classes['tab-scrollable'] = this.isScrollable;
		classes['tab-small'] = this.small;
		classes['tab-flush'] = this.flush;
		classes['tab-show-all'] = this.showAll;
		classes[`tab-${this.direction}`] = this.transition;

		return classes;
	}

	getBgColor(direction = 'left') {
		let value = '';
		value =
			direction === 'left' && this.scrollColor
				? `linear-gradient(to left, rgba(${this.scrollColor}, 0) 0%, rgba(${this.scrollColor}, 1) 40%)`
				: `linear-gradient(to left, rgba(${this.scrollColor}, 1) 60%, rgba(${this.scrollColor}, 0) 100%)`;

		return {
			'background-image': value
		};
	}

	toggleContentTransition() {
		this.contentTransitionState = !this.contentTransitionState;
	}

	// content animation direction
	determineNextActiveDirection(lastActiveIndex: number, nextActiveIndex: number): 'right' | 'left' {
		return nextActiveIndex > lastActiveIndex ? 'right' : 'left';
	}
	determineLastActiveDirection(lastActiveIndex: number, nextActiveIndex: number): 'right' | 'left' {
		return lastActiveIndex > nextActiveIndex ? 'right' : 'left';
	}

	preSwitchTab(tab: TabContentComponent, event: Event) {
		this.nextActiveTab = tab; // save for later
		const tabs = this.tabs.toArray();
		this.nextActiveIndex = tabs.indexOf(tab);
		tabs.forEach((tabItem, i) => {
			if (tabItem.isActive) {
				this.lastActiveIndex = i;
			}

			if (this.transition) {
				// make sure item has transition enabled
				tabItem.hasTransition = true;
				// start transition out of the previous tab
				this.direction = this.determineLastActiveDirection(i, this.nextActiveIndex);
				this.toggleContentTransition();
			} else if (!this.transition) {
				// make sure item has transition disabled
				tabItem.hasTransition = false;
			}

			// turn off the rest
			tabItem.isActive = false;
		});

		// if transition is disabled, no need to wait
		if (!this.transition) {
			this.nextActiveTab.isActive = true;
			this.tabupdate.emit({ active: this.nextActiveTab.name, id: this.nextActiveTab.id });
			delete this.nextActiveTab;
			delete this.nextActiveIndex;
			delete this.lastActiveIndex;
		}

		// if tab switch was triggered by click, make sure tab links are not being cut off
		if (event && this.isScrollable) {
			this.scrollLink(event.target);
		}
	}

	onTransitionEvent(e: ITransitionEvent) {
		switch (e.type) {
			case 'leave-complete': {
				if (this.lastActiveIndex != null && this.nextActiveIndex != null) {
					this.direction = this.determineNextActiveDirection(this.lastActiveIndex, this.nextActiveIndex);
					this.toggleContentTransition();
					delete this.lastActiveIndex;
					delete this.nextActiveIndex;
				}
				break;
			}
			case 'enter': {
				if (this.nextActiveTab) {
					this.nextActiveTab.isActive = true;
				}
				break;
			}
			case 'enter-complete': {
				this.tabupdate.emit({ active: this.nextActiveTab.name, id: this.nextActiveTab.id });
				delete this.nextActiveTab;
				break;
			}
		}
	}

	scrollTabs(dir) {
		const tabWrapWidth = this.tabList.nativeElement.offsetWidth;
		const start = this.tabList.nativeElement.scrollLeft;
		const distance = tabWrapWidth * 0.75;

		let step = tabWrapWidth / 15;
		step = dir === 'right' ? step : -step;

		this.scrollTo(this.tabWrap.nativeElement, step, dir, start, distance);
	}

	scrollLink(el) {
		const linkBox = el.getBoundingClientRect();
		const tabBox = this.tabWrap.nativeElement.getBoundingClientRect();
		const tabWrapBox = this.tabWrap.nativeElement.getBoundingClientRect();
		const arrowWidth = 35;
		const start = this.tabWrap.nativeElement.scrollLeft;

		// check if selected tab link is too far to the left
		if (!this.scrollDisabledLeft && linkBox.x < tabBox.x + arrowWidth) {
			const distance = tabBox.x - linkBox.x + arrowWidth;
			this.scrollTo(this.tabWrap.nativeElement, -10, 'left', start, distance);

			// check if selected tab link is too far to the right
		} else if (!this.scrollDisabledRight && linkBox.x + linkBox.width > tabWrapBox.x + tabWrapBox.width - arrowWidth) {
			const distance = linkBox.x + linkBox.width - (tabWrapBox.x + tabWrapBox.width) + arrowWidth;
			this.scrollTo(this.tabWrap.nativeElement, 10, 'right', start, distance);
		}
	}

	scrollTo(el, step, dir = 'right', start, dist) {
		const scrollWidth = this.tabWrap.nativeElement.scrollWidth;
		const elWidth = el.offsetWidth;
		const scrollPos = el.scrollLeft;

		// add step to current scroll positon to animate
		el.scrollLeft = scrollPos + step;

		// create next animation step
		const animationFrameFn = this.scrollTo.bind(this, ...arguments);
		// check if scroll position has reached its end value or the end of the wrapper element
		if (dir === 'right' && scrollPos <= start + dist && scrollPos + elWidth < scrollWidth) {
			requestAnimationFrame(animationFrameFn);
			// check if scroll position has reached its end value or the beginning of the wrapper element
		} else if (dir === 'left' && scrollPos >= start - dist && scrollPos > 0) {
			requestAnimationFrame(animationFrameFn);
		}
	}

	updateArrows() {
		// get widths and current scroll position
		const tabWrapWidth = Math.round(this.tabWrap.nativeElement.getBoundingClientRect().width);
		const scrollPos = this.tabWrap.nativeElement.scrollLeft;

		// disable left tab if scroll position is at 0
		this.scrollDisabledLeft = scrollPos <= 0;
		// disable right tab if scroll position is at end of scroll bar width
		this.scrollDisabledRight = scrollPos + tabWrapWidth >= this.tabWrap.nativeElement.scrollWidth;
	}
}
